// src/utils/formatDate.ts

import { format, formatDistanceToNow, parseISO, isValid, differenceInDays, differenceInHours, addDays, isBefore, isAfter, startOfDay } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';

/**
 * Format tanggal ke format Indonesia
 * @example formatDate('2026-02-28') => '28 Februari 2026'
 */
export const formatDate = (date: string | Date | null | undefined, formatStr: string = 'dd MMMM yyyy'): string => {
  if (!date) return '-';
  try {
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(parsed)) return '-';
    return format(parsed, formatStr, { locale: idLocale });
  } catch {
    return '-';
  }
};

/**
 * Format tanggal pendek
 * @example formatDateShort('2026-02-28') => '28 Feb 2026'
 */
export const formatDateShort = (date: string | Date | null | undefined): string => {
  return formatDate(date, 'dd MMM yyyy');
};

/**
 * Format tanggal dengan waktu
 * @example formatDateTime('2026-02-28T09:30:00') => '28 Februari 2026, 09:30'
 */
export const formatDateTime = (date: string | Date | null | undefined): string => {
  return formatDate(date, 'dd MMMM yyyy, HH:mm');
};

/**
 * Format tanggal dengan waktu pendek
 * @example formatDateTimeShort('2026-02-28T09:30:00') => '28 Feb 2026, 09:30'
 */
export const formatDateTimeShort = (date: string | Date | null | undefined): string => {
  return formatDate(date, 'dd MMM yyyy, HH:mm');
};

/**
 * Format waktu saja
 * @example formatTime('2026-02-28T09:30:00') => '09:30'
 */
export const formatTime = (date: string | Date | null | undefined): string => {
  return formatDate(date, 'HH:mm');
};

/**
 * Format relatif (x waktu yang lalu)
 * @example formatRelative('2026-02-25T09:00:00') => '3 hari yang lalu'
 */
export const formatRelative = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  try {
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(parsed)) return '-';
    return formatDistanceToNow(parsed, { addSuffix: true, locale: idLocale });
  } catch {
    return '-';
  }
};

/**
 * Format untuk input date HTML
 * @example formatForInput('2026-02-28') => '2026-02-28'
 */
export const formatForInput = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  try {
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(parsed)) return '';
    return format(parsed, 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

/**
 * Format untuk input datetime-local HTML
 * @example formatForDateTimeInput('2026-02-28T09:30:00') => '2026-02-28T09:30'
 */
export const formatForDateTimeInput = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  try {
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(parsed)) return '';
    return format(parsed, "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
};

/**
 * Hitung selisih hari dari sekarang
 * @example daysFromNow('2026-03-05') => 5 (jika sekarang 2026-02-28)
 */
export const daysFromNow = (date: string | Date | null | undefined): number => {
  if (!date) return 0;
  try {
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(parsed)) return 0;
    return differenceInDays(parsed, new Date());
  } catch {
    return 0;
  }
};

/**
 * Hitung selisih jam dari sekarang
 */
export const hoursFromNow = (date: string | Date | null | undefined): number => {
  if (!date) return 0;
  try {
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(parsed)) return 0;
    return differenceInHours(parsed, new Date());
  } catch {
    return 0;
  }
};

/**
 * Cek apakah tanggal sudah lewat (overdue)
 */
export const isOverdue = (date: string | Date | null | undefined): boolean => {
  if (!date) return false;
  try {
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(parsed)) return false;
    return isBefore(startOfDay(parsed), startOfDay(new Date()));
  } catch {
    return false;
  }
};

/**
 * Cek apakah tanggal mendekati deadline (dalam N hari)
 */
export const isApproachingDeadline = (date: string | Date | null | undefined, withinDays: number = 7): boolean => {
  if (!date) return false;
  try {
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(parsed)) return false;
    const today = startOfDay(new Date());
    const target = startOfDay(parsed);
    const diff = differenceInDays(target, today);
    return diff >= 0 && diff <= withinDays;
  } catch {
    return false;
  }
};

/**
 * Format durasi overdue
 * @example formatOverdue('2026-02-20') => '8 hari terlambat' (jika sekarang 28 Feb)
 */
export const formatOverdue = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  const days = Math.abs(daysFromNow(date));
  if (days === 0) return 'Hari ini';
  if (days === 1) return '1 hari terlambat';
  return `${days} hari terlambat`;
};

/**
 * Format sisa waktu
 * @example formatRemaining('2026-03-05') => '5 hari lagi' (jika sekarang 28 Feb)
 */
export const formatRemaining = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  const days = daysFromNow(date);
  if (days < 0) return formatOverdue(date);
  if (days === 0) return 'Hari ini';
  if (days === 1) return '1 hari lagi';
  return `${days} hari lagi`;
};

/**
 * Get bulan dan tahun dalam bahasa Indonesia
 * @example getMonthYear(new Date()) => 'Februari 2026'
 */
export const getMonthYear = (date: Date = new Date()): string => {
  return format(date, 'MMMM yyyy', { locale: idLocale });
};

/**
 * Get nama hari dalam bahasa Indonesia
 * @example getDayName(new Date()) => 'Jumat'
 */
export const getDayName = (date: Date = new Date()): string => {
  return format(date, 'EEEE', { locale: idLocale });
};

/**
 * Get tanggal hari ini dalam format input
 */
export const getTodayInput = (): string => {
  return format(new Date(), 'yyyy-MM-dd');
};

/**
 * Get tanggal N hari dari sekarang dalam format input
 */
export const getDateAfterDays = (days: number): string => {
  return format(addDays(new Date(), days), 'yyyy-MM-dd');
};